import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Popover,
  Tooltip,
  IconButton,
  Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import DownloadOptions from "./WidgetMenu/DownloadOptions";
import useAxiosPrivate from "../../../../Hooks/useAxiosPrivate";
import { makeStyles } from "@mui/styles";
import Loading from "../../../Notification-Loading/Loading/Loading";
import constants from "../../../../constants";
import PaginationCustom from "./PaginationCustom";
import AlertContext from "../../../../context/AlertContext";
import { Columns3 } from "lucide-react";
import moment from "moment-timezone";
import Select from "react-select";
import { getDataFromStorage } from "../../../../util";
import Search from "../../../CommonComponents/Search";
import Button from "../../../Button";
import ApplyFilters from "../../../CommonComponents/ApplyFilters";

import NameCell from "../../../CommonComponents/NameCell";
import { UserReportProvider } from "../../../../context/UserReportContext";

// Memoized table cell styles
const useStyles = makeStyles({
  Name: {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "20px",
    textAlign: "left",
    color: "#101828",
    padding: "9px",
    borderBottom: "1px solid rgb(224, 224, 224)",
    boxShadow: "inset -1px 0 0 0 rgb(224, 224, 224)",
  },
  Email_address: {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: "400",
    lineHeight: "20px",
    textAlign: "left",
    color: "#667085",
    padding: "9px",
    boxShadow: "inset -1px 0 0 0 rgb(224, 224, 224)",
  },
  College: {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: "400",
    lineHeight: "20px",
    textAlign: "left",
    color: "#667085",
    padding: "9px",
    boxShadow: "inset -1px 0 0 0 rgb(224, 224, 224)",
  },
  Branch: {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: "400",
    lineHeight: "20px",
    textAlign: "left",
    color: "#667085",
    padding: "9px",
    boxShadow: "inset -1px 0 0 0 rgb(224, 224, 224)",
  },
  Rank: {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: "400",
    lineHeight: "20px",
    textAlign: "center",
    color: "#101828",
    padding: "9px",
    boxShadow: "inset -1px 0 0 0 rgb(224, 224, 224)",
  },
  totalscore: {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: "400",
    lineHeight: "20px",
    textAlign: "center",
    color: "#344054",
    padding: "9px",
    boxShadow: "inset -1px 0 0 0 rgb(224, 224, 224)",
  },
  Percentile: {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: "400",
    lineHeight: "20px",
    textAlign: "center",
    color: "#344054",
    padding: "9px",
    boxShadow: "inset -1px 0 0 0 rgb(224, 224, 224)",
  },
  // Add a default style for center-aligned columns
  Center: {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: "400",
    lineHeight: "20px",
    textAlign: "center",
    color: "#344054",
    padding: "9px",
    boxShadow: "inset -1px 0 0 0 rgb(224, 224, 224)",
  },
});

function LeaderBoardndPercentile(Props) {
  const axios = useAxiosPrivate();
  const classes = useStyles();
  const Alert = useContext(AlertContext);

  const [page, setPage] = useState(1);
  const [showDownloadOptionsDropDown, setShowDownloadOptionsDropDown] = useState(false);
  const [widgetColumns, setWidgetColumns] = useState([]);
  const [originalData, setOriginalData] = useState([]); // Store original data
  const [filteredData, setFilteredData] = useState([]);
  const [totalDataCount, setDataTotalCount] = useState(0);
  const [orientation, setOrientation] = useState("landscape");
  // const [render, setRender] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [minIndex, setMinIndex] = useState(0);
  const [maxIndex, setMaxIndex] = useState(0);
  const [sortBy, setSortBy] = useState(
    Props.data.type == constants.BOARD_WIDGETS.LEADERBOARD
      ? "ranking"
      : "percentile"
  );
  const [search, setSearch] = useState("");
  const [orderBy, setOrderBy] = useState("ASC");
  // Filter states
  const [collegeList, setCollegeList] = useState([]);
  const [college, setCollege] = useState({ label: "All", value: 0 });
  const [cachedTime, setCachedTime] = useState(null);
  const [filtersApplied, setFiltersApplied] = useState(true);
  const [filtersChanged, setFiltersChanged] = useState(false);

  // column visibility
  const [columnVisibility, setColumnVisibility] = useState({
    name: true,
    email: false,
    branch: false,
    college: false,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const limit = Props.batchDashboard ? 10 : 10;

  const [refresh, setRefresh] = useState(false);
  const requestIdRef = useRef(0);

  // Memoized constants
  const isLeaderboard = useMemo(
    () => Props.data.type === constants.BOARD_WIDGETS.LEADERBOARD,
    [Props.data.type]
  );

  const isHr = useMemo(() => getDataFromStorage("role") === constants.Roles.hr, []);
  const isGradious = useMemo(() => parseInt(getDataFromStorage("companyId")) === constants.GRADIOUS_ACCOUNT_ID, []);

  // Column name mapping
  const getName = useCallback((column) => {
    const columnMap = {
      Name: "name",
      "Email address": "email",
      Branch: "branch",
      College: "college",
      Percentile: "percentile",
      Rank: "ranking",
      "Total points": "totalPointsEarned",
    };
    return columnMap[column] || column.toLowerCase();
  }, []);

  // Get the appropriate CSS class for each column
  const getCellClassName = useCallback(
    (column) => {
      const columnClassMap = {
        Name: classes.Name,
        "Email address": classes.Email_address,
        Branch: classes.Branch,
        College: classes.College,
        Rank: classes.Rank,
        "Total points": classes.totalscore,
        Percentile: classes.Percentile,
      };

      return columnClassMap[column] || classes.Center;
    },
    [classes]
  );

  // Handle API responses
  const handleResponse = useCallback(
    (title, msg) => {
      Alert.setShowNotify({
        show: true,
        title: title,
        msg: msg,
      });
    },
    [Alert]
  );

  // Apply filters handler
  // const handleApplyFilters = () => {
  //   console.log("Applying filters with college:", college);
  //   setPage(1);
  //   setFiltersApplied(true);
  //   setRefresh(true);
  // };

  // Filter handlers
  const handleInputChange = useCallback((value, name) => {
    if (name === "college") {
      setCollege(value);
    }
  }, []);

  // Column visibility handlers
  const handleToggleColumn = useCallback((column) => {
    if (column !== "name") {
      setColumnVisibility((prev) => ({
        ...prev,
        [column]: !prev[column],
      }));
    }
  }, []);

  const handleSettingsClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleSettingsClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Get visible columns based on visibility settings
  const getVisibleColumns = useCallback(() => {
    const columns = [];
    if (isLeaderboard) columns.push("Rank");
    if (columnVisibility.name) columns.push("Name");
    if (columnVisibility.email) columns.push("Email address");
    if (columnVisibility.branch) columns.push("Branch");
    if (columnVisibility.college) columns.push("College");
    if (!isLeaderboard) columns.push("Percentile");
    columns.push("Total points");
    return columns;
  }, [isLeaderboard, columnVisibility]);

  // Search functionality
  const handleSearch = useCallback(
    (value) => {
      setSearch(value);
      setPage(1); // Reset to first page when searching

      if (!value.trim()) {
        // If search is empty, show all original data
        setFilteredData(originalData);
        setDataTotalCount(originalData.length);
        return;
      }

      // console.log("Searching for:", originalData);

      const searchTerm = value.toLowerCase().trim();
      const filtered = originalData.filter((row) =>
        row.name.toLowerCase().includes(searchTerm)
      );

      setFilteredData(filtered);
      setDataTotalCount(filtered.length);
    },
    [originalData]
  );

  // Sort functionality
  const sortByselectedColumn = useCallback(
    (col) => {
      const column = getName(col);
      const newOrderBy =
        column === sortBy ? (orderBy === "ASC" ? "DESC" : "ASC") : "ASC";

      const sortedData = [...filteredData].sort((a, b) => {
        const aVal = a[column];
        const bVal = b[column];

        if (typeof aVal === "number" && typeof bVal === "number") {
          return newOrderBy === "ASC" ? aVal - bVal : bVal - aVal;
        } else {
          const aStr = String(aVal || "").trim();
          const bStr = String(bVal || "").trim();
          return newOrderBy === "ASC"
            ? aStr.localeCompare(bStr)
            : bStr.localeCompare(aStr);
        }
      });

      setSortBy(column);
      setOrderBy(newOrderBy);
      setFilteredData(sortedData);
    },
    [filteredData, getName, sortBy, orderBy]
  );

  // Pagination handlers
  const handleNext = useCallback(() => {
    const totalPageCount = Math.ceil(totalDataCount / limit);
    const newMin = page * limit;
    const newMax = Math.min((page + 1) * limit, totalDataCount);
    setMinIndex(newMin);
    setMaxIndex(newMax);
    setPage((prev) => Math.min(prev + 1, totalPageCount));
  }, [page, totalDataCount, limit]);

  const handlePrevious = useCallback(() => {
    const newMin = Math.max((page - 2) * limit, 0);
    const newMax = (page - 1) * limit;
    setMinIndex(newMin);
    setMaxIndex(newMax);
    setPage((prev) => Math.max(prev - 1, 1));
  }, [page, limit]);

  const allOption = { label: "All", value: 0 };


  const responseOfWidget = async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    try {
      setIsLoading(true);
      const params = Props.batchDashboard
        ? {
          type: Props.data.type,
          batchId: Props.batchId,
          college: college && college?.value !== 0 ? college?.label : null,
          refresh,
        }
        : {
          type: Props.data.type,
        };

      const endpoint = !Props.batchDashboard
        ? `node/admin/dashboard/${Props.dashboardId}/widget/${Props.data.wid}/report`
        : `node/admin/batch/dashboard/report`;

      const response = await axios.get(endpoint, { params });

      if (requestId !== requestIdRef.current) return;

      if (response.data?.resultCode === constants.RESULT_STATUS.TECHNICAL_ERROR) {
        throw new Error(response.data?.msg || "Technical Error");
      }

      console.log("Widget Response:", response);

      if (response.data?.createdAt) {
        setCachedTime(
          moment(response.data?.createdAt)
            .tz("Asia/Kolkata")
            .format("YYYY-MM-DD HH:mm:ss")
        );
      }

      if (response?.data?.data) {
        const widgetData = response.data.data.data || [];

        // Store original data and set filtered data
        setOriginalData(widgetData);
        setFilteredData(widgetData);
        setDataTotalCount(
          response.data.data.learnersTotalCount || widgetData.length
        );

        const typeOfOrientation =
          response.data.data.orientation === constants.ORIENTATION.LANDSCAPE
            ? "landscape"
            : "portrait";
        setOrientation(typeOfOrientation);

        setMinIndex(0);
        setMaxIndex(limit);

        // Determine columns from data
        const firstRow = widgetData[0] || {};
        const columns = [];

        if (isLeaderboard) columns.push("Rank");
        if (firstRow.name !== undefined) columns.push("Name");
        if (firstRow.email !== undefined) columns.push("Email address");
        if (firstRow.branch !== undefined) columns.push("Branch");
        if (firstRow.college !== undefined) columns.push("College");
        if (!isLeaderboard) columns.push("Percentile");
        if (firstRow.totalPointsEarned !== undefined)
          columns.push("Total points");

        setWidgetColumns(columns);

        if (Props.refresh) {
          Props.setRefresh(false);
        }
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error(error);
      if (error.message.includes("403")) {
        handleResponse(
          "Error",
          "You have been logged-out due to inactivity. Login again."
        );
        Alert.setIsUnAuth(true);
      } else {
        handleResponse("Error", error.message);
      }
    } finally {
      if (requestId !== requestIdRef.current) return;
      setIsLoading(false);
      if (!isHr && isGradious) {
        setFiltersApplied(false);
      }
      setFiltersChanged(true);
      if (!college) setCollege(allOption);
    }
  };

  // Fetch college data
  useEffect(() => {
    setIsLoading(true);
    if (Props.batchDashboard && !isHr && isGradious) {
      const responseOfColleges = async () => {
        try {
          const response = await axios.get("node/admin/batch/college", {
            params: { id: Props.batchId,filter:true },
            headers: { "Content-type": "application/json" },
          });

          const transformedData = response.data.data.map((item, index) => ({
            label: item.name,
            value: index + 1,
          }));



          setCollegeList([allOption, ...transformedData]);
          if (transformedData.length === 1) {
            setCollege(transformedData[0]);
          } else {
            setCollege(allOption);
          }
        } catch (err) {
          if (err.message.includes("403")) {
            handleResponse(
              "Error",
              "You have been logged-out due to inactivity. Login again."
            );
            Alert.setIsUnAuth(true);
          } else {
            handleResponse("Error", err.message);
          }
        }
      };
      responseOfColleges();
    }
    setIsLoading(false);
  }, []);

  // Main data fetching effect
  useEffect(() => {
    // console.log("filtersApplied", filtersApplied);
    // if isHr and not isGradious, do fetch data directly
    if ((!isHr && isGradious) && !filtersApplied) return;
    responseOfWidget();
  }, [
    Props.batchId,
    Props.dashboardId,
    Props.data.wid,
    Props.refresh,
    refresh,
    filtersApplied,
    Props.data.type,
    college?.label,
    college?.value,
  ]);

  // Reset pagination when filtered data changes
  useEffect(() => {
    setPage(1);
    setMinIndex(0);
    setMaxIndex(Math.min(limit, filteredData.length));
  }, [filteredData, limit]);

  // Memoized visible columns
  const visibleColumns = useMemo(
    () => (Props.batchDashboard ? getVisibleColumns() : widgetColumns),
    [Props.batchDashboard, getVisibleColumns, widgetColumns]
  );

  // Memoized paginated data
  const paginatedData = useMemo(
    () => filteredData.slice(minIndex, maxIndex),
    [filteredData, minIndex, maxIndex]
  );

  // Determine alignment for each column
  const getColumnAlignment = useCallback((column) => {
    const leftAlignedColumns = ["Name", "Branch", "College", "Email address"];
    return leftAlignedColumns.includes(column) ? "left" : "center";
  }, []);

  // useEffect(() => {
  //   setRender(false);
  //   const responseOfWidget = async () => {
  //     try {
  //       let response = await axios.get(
  //          !Props.batchDashboard?
  //         `node/admin/dashboard/${Props.dashboardId}/widget/${Props.data.wid}/report`
  //         : `node/admin/batch/dashboard/report`,
  //         {
  //           params: params,
  //           // headers: {
  //           //   "Content-type": "application/json",
  //           // },
  //         }
  //       );
  //       if (response?.data?.data != undefined) {
  //         setWidgetData(response?.data?.data?.data);
  //         setFilteredData(response?.data?.data?.data);
  //         console.log("response.data.data.data", response?.data?.data?.learnersTotalCount);
  //         setDataTotalCount(response?.data?.data?.learnersTotalCount || response?.data?.data?.data?.length);
  //         let typeOfOrientation =
  //           response?.data?.data?.orientation == constants.ORIENTATION.LANDSCAPE
  //             ? "landscape"
  //             : "portrait";

  //         setOrientation(typeOfOrientation);
  //         setMinIndex(0);
  //         setMaxIndex(limit);

  //         const columns = [];

  //         let columnfromResponse = response?.data?.data?.data[0];

  //         if (Props.data.type == constants.BOARD_WIDGETS.LEADERBOARD) {
  //           columns.push("Rank");
  //         }

  //         if (columnfromResponse?.hasOwnProperty("name")) {
  //           columns.push("Name");
  //         }

  //         if (columnfromResponse?.hasOwnProperty("email")) {
  //           columns.push("Email address");
  //         }
  //         if (columnfromResponse?.hasOwnProperty("branch")) {
  //           columns.push("Branch");
  //         }
  //         if (columnfromResponse?.hasOwnProperty("college")) {
  //           columns.push("College");
  //         }
  //         if (Props.data.type == constants.BOARD_WIDGETS.PERCENTILE) {
  //           columns.push("Percentile");
  //         }
  //         if (columnfromResponse?.hasOwnProperty("totalPointsEarned")) {
  //           columns.push(`Total points`);
  //         }
  //         setWidgetColumns(columns);

  //         if (Props.refresh) {
  //           Props.setRefresh(false);
  //         }
  //       }
  //       setRender(true);
  //     } catch (error) {
  //       console.error(error);
  //       Alert.setShowNotify({
  //         show:true,
  //         title:"Error",
  //         msg:error.message
  //       });
  //       setRender(true);
  //     }
  //   };

  //   responseOfWidget();
  // }, [Props.data.wid, Props.refresh, college]);

  // Get the columns to display

  return (
    <div className={isLoading ? "leaderBoardndPercentile_loading" : "leaderBoardContainer"}>
      <div className="boardHeader">
        <div className="leaderBoardwidgetNamendDescription">
          {/* <div>
          <text id="leaderboardText1"> {Props.data.name}</text>
          {Props.data.description && (
            <text id="leaderboardText2"> {Props.data.description}</text>
          )}
          </div> */}
          <Search
            placeholder="Search Student"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            iconPosition="end"
          />

          {Props.batchDashboard && (
            <div className="leaderBoardFilters">
              {Props.data.type === constants.BOARD_WIDGETS.LEADERBOARD && (
                <>
                  {cachedTime && (
                    <Typography className="leaderboard-box-header-cached-time">
                      Updated at {cachedTime}
                    </Typography>
                  )}
                  <Tooltip title="Refresh" isIcon={true}>
                    <IconButton
                      hierarchy="tertiaryGrey"
                      onClick={() => {
                        setRefresh(true)
                        setFiltersApplied(true)
                      }
                      }
                      aria-label="Refresh leaderboard"
                    // disabled={!render}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}

              
    
              {!isHr &&  isGradious &&(
                  <div className="collegeList">
                    <Select
                      isClearable
                      options={collegeList}
                      placeholder={"College"}
                      value={college}
                      isDisabled={!collegeList}
                      onChange={(e) => {
                        handleInputChange(e, "college");
                        setPage(1);
                        setRefresh(true);
                        setFiltersApplied(true);
                      }}
                      styles={{
                        container: (base) => ({
                          ...base,
                          width: "150px", // set custom width here
                        }),
                        menu: (base) => ({
                          ...base,
                          width: "150px", // dropdown menu width
                        }),
                      }}
                    />
                  </div>
              )}

              {/* Add Apply Filter button only for users who are not HR and not Gradious */}

              {/* {!isHr && isGradious && (
                <div className="filter-buttons" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Button
                    size={"sm"}
                    disable={isLoading}
                    hierarchy={{
                      type: "primary",
                      buttonText: isLoading ? 'Applying...' : 'Apply Filters',
                    }}
                    onClick={handleApplyFilters}
                  />
                </div>
              )} */}

              <IconButton
                onClick={handleSettingsClick}
                size="small"
                sx={{ ml: 0 }}
                aria-label="column settings"
              >
                <Tooltip title="Hide/Show Columns">
                  <Columns3 />
                </Tooltip>
              </IconButton>

              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleSettingsClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <div style={{ padding: "16px" }}>
                  <h5 style={{ margin: "0 0 12px 0" }}>Display Columns</h5>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={columnVisibility.name}
                          onChange={() => handleToggleColumn("name")}
                        />
                      }
                      label="Name"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={columnVisibility.email}
                          onChange={() => handleToggleColumn("email")}
                        />
                      }
                      label="Email"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={columnVisibility.branch}
                          onChange={() => handleToggleColumn("branch")}
                        />
                      }
                      label="Branch"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={columnVisibility.college}
                          onChange={() => handleToggleColumn("college")}
                        />
                      }
                      label="College"
                    />
                  </FormGroup>
                </div>
              </Popover>
            </div>
          )}
        </div>

        <div className="downloadDropdown">
          {showDownloadOptionsDropDown && (
            <div
              onMouseLeave={() => {
                setShowDownloadOptionsDropDown(false);
              }}
            >
              <DownloadOptions
                name={Props?.data?.name}
                data={filteredData}
                orientation={orientation}
              />
            </div>
          )}
          <Tooltip placement="bottom" title="Download" isIcon={true}>
            <IconButton
              hierarchy="tertiaryGrey"
              className="boarddownloadicon"
              onClick={() => setShowDownloadOptionsDropDown(true)}
              disabled={isLoading || filteredData.length === 0}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </div>


      </div>
      {isLoading && <Loading overlay />}
      {!isLoading &&
        <div className="tableContainer">
          {filteredData.length === 0 ? (
            <TableRow align="center" style={{ height: "450px", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              {!filtersApplied && !filtersChanged ? <ApplyFilters type={Props.data.type} text={`${(!isHr && isGradious) ? 'Choose college from the filter above and hit Apply to load the' : 'Click on Apply to load the'} ${Props.data.name} Report.`} />
                : filtersChanged && <ApplyFilters text="No data available for the selected filters. Try changing the filter options." />
              }
            </TableRow>
          ) : (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead style={{ background: "#f9fafb" }}>
                  <TableRow style={{ height: "44px" }}>
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column}
                        onClick={() => {
                          sortByselectedColumn(column);
                        }}
                        style={{
                          //width: `${1140 / widgetColumns.length}px`,
                          //styleName: Text sm/Medium;
                          fontFamily: "Inter",
                          fontSize: "14px",
                          textAlign: getColumnAlignment(column),
                          fontWeight: "500",
                          lineHeight: "20px",
                          // textAlign: "left",
                          color: "#344054",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          padding: "9px",
                          cursor: "pointer",
                          boxShadow:
                            "inset -1px 0 0 0 rgb(224, 224, 224),inset 0 1px 0 0 rgb(224, 224, 224)",
                          // maxWidth: `${1140 / widgetColumns.length}px`
                        }}
                      >
                        {column}{" "}
                        {sortBy === getName(column) &&
                          (orderBy === "ASC" ? "↑" : "↓")}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      style={{ height: "40px", textAlign: "center" }}
                    >
                      {/* {visibleColumns?.map((column) => (
                        <TableCell
                          key={column}
                          align={getColumnAlignment(column)}
                          className={getCellClassName(column)}
                        >
                          {row[getName(column)]}
                        </TableCell>
                      ))} */}
                      {visibleColumns?.map((column) => {
                        if (column === "Name") {
                          return (
                            <UserReportProvider key={column}>
                              <NameCell
                                row={{
                                  ...row,
                                  Name: row.name,
                                  learnerid: row.id,
                                }}
                                rowIndex={rowIndex}
                                className={classes.Name}
                                pos="batch-report-page-container"
                              />
                            </UserReportProvider>
                          );
                        }

                        return (
                          <TableCell
                            key={column}
                            align={getColumnAlignment(column)}
                            className={getCellClassName(column)}
                          >
                            {row[getName(column)]}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      }

      <div className="boardFooter">
        {filteredData.length > 0 && (
          <PaginationCustom
            from="leaderboard"
            setMaxIndex={setMaxIndex}
            setMinIndex={setMinIndex}
            totalPages={Math.ceil(totalDataCount / limit)}
            currentPage={page}
            setCurrentPage={setPage}
            handleNext={handleNext}
            handlePrevious={handlePrevious}
            limit={limit}
          />
        )}
      </div>
    </div>
  );
}

export default React.memo(LeaderBoardndPercentile);


// Download Icon Component
const DownloadIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17 12.5V15.8333C17 16.2754 16.8244 16.6993 16.5118 17.0118C16.1993 17.3244 15.7754 17.5 15.3333 17.5H4.66667C4.22464 17.5 3.80072 17.3244 3.48816 17.0118C3.17559 16.6993 3 16.2754 3 15.8333V12.5M6.33333 8.33333L10.5 12.5M10.5 12.5L14.6667 8.33333M10.5 12.5V2.5"
      stroke="#667085"
      strokeWidth="1.67"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Refresh Icon Component
const RefreshIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.6665 8.33333C1.6665 8.33333 3.33732 6.05685 4.6947 4.69854C6.05208 3.34022 7.92783 2.5 9.99984 2.5C14.142 2.5 17.4998 5.85786 17.4998 10C17.4998 14.1421 14.142 17.5 9.99984 17.5C6.58059 17.5 3.69576 15.2119 2.79298 12.0833M1.6665 8.33333V3.33333M1.6665 8.33333H6.6665"
      stroke="#667085"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);